import { Timings } from "./timings";
import { SinBinSystem } from "./enums/sinBinSystem";
import { Match } from "./match";
import { EventTimekeeping } from "./enums/eventTimekeeping";

export class TemplateConfig {
    timings: Timings;
    periodsNo: string; // legacy, this should really change
    withGoalScorers: boolean;
    teamSize: number;
    subsNo: number;
    subOpportunities: boolean;
    subOpportunitiesCount?: number;
    subOpportunitiesInETCount?: number;
    extraTimeAvailable: boolean;
    penaltiesAvailable: boolean;
    sinBinSystem: SinBinSystem;
    misconductCodeId?: string;
    eventTimekeeping?: EventTimekeeping; // absent = running clock

    constructor(
        timings: Timings,
        periodsNo: string,
        withGoalScorers: boolean,
        teamSize: number,
        subsNo: number,
        subOpportunities: boolean,
        subOpportunitiesCount: number | undefined,
        subOpportunitiesInETCount: number | undefined,
        extraTimeAvailable: boolean,
        penaltiesAvailable: boolean,
        sinBinSystem: SinBinSystem = SinBinSystem.none,
        misconductCodeId: string | undefined,
        eventTimekeeping?: EventTimekeeping
    ) {
        this.timings = timings;
        this.periodsNo = periodsNo;
        this.withGoalScorers = withGoalScorers;
        this.teamSize = teamSize;
        this.subsNo = subsNo;
        this.subOpportunities = subOpportunities;
        this.subOpportunitiesCount = subOpportunitiesCount;
        this.subOpportunitiesInETCount = subOpportunitiesInETCount;
        this.extraTimeAvailable = extraTimeAvailable;
        this.penaltiesAvailable = penaltiesAvailable;
        this.sinBinSystem = sinBinSystem;
        this.misconductCodeId = misconductCodeId;
        this.eventTimekeeping = eventTimekeeping;
    }

    static fromJSON(json: any) {
        return new TemplateConfig(
            json.timings,
            json.periodsNo,
            json.withGoalScorers,
            json.teamSize,
            json.subsNo,
            json.subOpportunities ?? false,
            json.subOpportunitiesCount,
            json.subOpportunitiesInETCount,
            json.extraTimeAvailable,
            json.penaltiesAvailable,
            json.sinBinSystem,
            json.misconductCodeId,
            json.eventTimekeeping
        );
    }

    static fromMatch(match: Match) {
        let halves = match.halves ?? [];
        if (halves.length === 0) {
            throw new Error(
                "Cannot derive a template from a match with no halves"
            );
        }

        // A match's halves array is generateHalves(periodsNo, timings) — playing periods
        // separated by one interval each, [p1, i1, p2, i2, ...] — optionally followed by
        // the two extra-time halves the watch appends when ET is played ([..., et1, et2],
        // with no interval before/between). Derive periodsNo and the period/interval slots
        // from the regular (non-ET) part only, so a "2 periods + ET" match (5 halves) is
        // not mistaken for a "3 periods" match.
        let regularHalves = halves.filter((half) => !half.isExtraTime);
        let extraTimeHalves = halves.filter((half) => half.isExtraTime);
        // Defensive: a malformed match with no non-ET halves still needs *something*.
        let periods = regularHalves.length > 0 ? regularHalves : halves;

        let matchPeriod1 = periods[0].length / 60000;
        // periods[0],[1],[2]
        // p1, i1, p2
        let matchPeriod2 =
            periods.length >= 3 ? periods[2].length / 60000 : undefined;
        // periods[0],[1],[2],[3],[4]
        // p1, i1, p2, i2, p3
        let matchPeriod3 =
            periods.length >= 5 ? periods[4].length / 60000 : undefined;
        // periods[0],[1],[2],[3],[4],[5],[6]
        // p1, i1, p2, i2, p3, i3, p4
        let matchPeriod4 =
            periods.length >= 7 ? periods[6].length / 60000 : undefined;

        let matchInterval1 =
            periods.length >= 3 ? periods[1].length / 60000 : undefined;
        let matchInterval2 =
            periods.length >= 5 ? periods[3].length / 60000 : undefined;
        let matchInterval3 =
            periods.length >= 7 ? periods[5].length / 60000 : undefined;

        let extraTimeHalfLengthMs =
            match.extraTimeHalfLength ?? extraTimeHalves[0]?.length;

        let timings = new Timings(
            matchPeriod1,
            matchPeriod2,
            matchInterval1,
            matchPeriod3,
            matchPeriod4,
            matchInterval2,
            matchInterval3,
            extraTimeHalfLengthMs
                ? extraTimeHalfLengthMs / 60000
                : match.extraTimeAvailable
                ? 15
                : undefined,
            match.sinBinSystem === "none" ? undefined : 10
        );

        let periodsNo = 2;

        if (periods.length === 1) {
            periodsNo = 1;
        } else if (periods.length === 3) {
            periodsNo = 2;
        } else if (periods.length === 5) {
            periodsNo = 3;
        } else if (periods.length === 7) {
            periodsNo = 4;
        }

        let sinBinSystem = match.sinBinSystem || SinBinSystem.none;

        return new TemplateConfig(
            timings,
            "" + periodsNo,
            false,
            11,
            5,
            match.subOpportunities ?? false,
            match.subOpportunitiesCount,
            match.subOpportunitiesInETCount,
            match.extraTimeAvailable,
            match.penaltiesAvailable,
            sinBinSystem,
            match.misconductCodeId || "fifa",
            match.eventTimekeeping
        );
    }
}
